// Dependencies
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect'
import moment from 'moment';

// Material UI
import { Grid, Typography } from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';

//Utils
import { getEventColor } from 'Components/Map/utils';
import { TypeObjectMap } from 'Components/Map/providers/enums';
import { translateEvent } from 'Modules/.common/utils/translateEvents';
import { MapUtilitiesTypes } from '../MapUtilitiesMenu/enums';
import useExport from 'Components/Table/Export';

// Components
import { SidePanelFooter } from 'Components/SidePanel';
import TableView from 'Components/TableView';
import { Button, Tooltip } from 'Components';
import AreaMenu, { Area, Menu } from 'Components/AreaMenu';
import MenuItem from '@mui/material/MenuItem';

//Styles
import './unitSelection.scss'

//Actions
import { setListUnits, selectModuleMapSettings, selectCreateClickMapType, updateShowVehicleTrailsSettings } from "Redux/actions";

/*********************************************************
 * Area selectors / hooks
*********************************************************/
import { useDataGridThrottler } from 'Core/hooks'
import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';
import { getUnitsNaturalOrder } from 'Core/data/Helpers';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);

const getData = createSelector(
  [getUnitsCatalog, getReadingsCatalog],
  (units, readings) => {
    let data = []
    units.map(unit => {
      if (unit.show != undefined && unit.show === 0) {
        return
      }
      let lastReading = readings[unit.id]
      let dataToPush = {
        ...unit,
        landmarkname: lastReading?.landmarkname,
        unitTime: lastReading?.unitTime,
        address: lastReading?.address?.formatted || lastReading?.address?.address,
        eventName: lastReading?.eventName,
      }
      data.push(dataToPush)
    })
    return data
  }
)


/********************************************************
 * Format the UnitTime field
 ********************************************************/
const getDate = (dateIn) => {
  let date = moment(dateIn)
  if (!date.isValid()) {
    if (dateIn && dateIn.length > 10) {
      date = moment.unix(Number(dateIn?.substring(0, dateIn.length - 3)))
    } else {
      date = moment.unix(Number(dateIn))
    }
  }

  return date;
}

const getMomentLocale = (locale) => {
  const normalizedLocale = (locale || 'en').toLowerCase()

  if (normalizedLocale.startsWith('es')) return 'es'
  if (normalizedLocale.startsWith('en')) return 'en'

  return normalizedLocale
}

const getLocalizedDateParts = (date, locale, atLabel) => {
  if (!date?.isValid()) {
    return {
      duration: null,
      formatDate: null,
    }
  }

  const localizedDate = date.clone().locale(getMomentLocale(locale))

  return {
    duration: localizedDate.fromNow(),
    formatDate: localizedDate.format(`dddd, MMM D [${atLabel || 'at'}] h:mm a`),
  }
}

/********************************************************
 * Icon marker in the table of selected units
 ********************************************************/
const IconMarker = (props) => {
  const color = props?.color || 'orangered'
  return (
    <svg className="markerIcon" xmlns="http://www.w3.org/2000/svg"
      //transform={`rotate(${props.heading})`}
      width="40" height="40" viewBox="0 0 40 40" aria-labelledby="title">
      <title id="title">{props?.name}</title>
      <path fill={color} d="M 2.64,20.41
                    C 2.64,20.41 19.57,11.59 19.57,11.59
                    19.57,11.59 2.64,3.27 2.64,3.27
                    2.64,3.27 5.46,11.59 5.46,11.59
                    5.46,11.59 2.64,20.41 2.64,20.41 Z"/>
    </svg>
  )
}

const UnitSelection = (props) => {
  const { messages, mapRef } = props
  const dispatch = useDispatch();
  const [unitList, setUnitList] = useState([])
  const locale = useSelector(state => state.commomRedux.language);

  const units = useSelector(state => state.realtimeMapUnitSelectionRedux.units);
  const show = useSelector(state => state.realtimeMapUnitSelectionRedux.show);

  /********************************************************
   * Definition of the headers of the table to be exported
   ********************************************************/
  const getHeaders = () => {
    return [
      messages["realtimemaps_unit_selection_unit"],
      messages["realtimemaps_unit_selection_event"],
      messages["realtimemaps_unit_selection_address"],
      messages["realtimemaps_unit_selection_unitDate"]
    ];
  }

  /********************************************************
  * Create a computed data from catalog
  ********************************************************/

  const computedData = useSelector(state => getData(state) || []);
  const propertiesToValidate = useMemo(() => ["label"], []);

  // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
  const data = useDataGridThrottler(
    computedData,
    1000,
    () => { },
    "id",
    propertiesToValidate
  );

  /********************************************************
   * Format of the table data to be exported
   ********************************************************/
  const getDataToExport = (rows) => {
    let items = unitList ? unitList : [];
    let data = [];
    items.forEach((item) => {

      let date = getDate(item.unitTime)
      let { duration, formatDate } = getLocalizedDateParts(date, locale, messages['at']);
      let address = `${item?.address || ''} ${item?.landmarkname ? `(${item?.landmarkname})` : ''} `
      let unit = item?.label || ''
      let eventName = `${translateEvent(item?.eventName, messages) || ''} ${duration ? `(${duration})` : ""}`

      data.push([unit, eventName, address, formatDate]);
    });
    return data;
  }

  /*********************************************************
   * Button to export
  *********************************************************/
  const onExport = (option) => {
    if (option === "CSV") exportCSVPdf.exportCSV();
    else exportCSVPdf.exportPDF();
  }

  /********************************************************
   * Use hook to export PDF
   ********************************************************/
  const exportCSVPdf = useExport(
    getHeaders,
    getDataToExport,
    [],
    [],
    null,
    messages['realtimemaps_unit_selection_exportOfUnits'],
    null,
    true
  );

  /********************************************************
   * Restore initial state
   ********************************************************/
  const handleSection = () => {
    mapRef.current.removeMapObject('drawn_polygon', TypeObjectMap.Polygon)
    mapRef.current.removeMapDraw();
    dispatch(setListUnits([], false));
    dispatch(selectCreateClickMapType(TypeObjectMap.Default))
    dispatch(selectModuleMapSettings(MapUtilitiesTypes.Default))
    dispatch(updateShowVehicleTrailsSettings(false))
  }

  /********************************************************
   * Export options
   ********************************************************/
  const exportOptions = ["PDF", "CSV"];

  useEffect(() => {
    if (units) {
      let selectedUnits = [];
      data.map((unit) => {
        let unitValidation = units.includes(+unit.id)
        if (unitValidation) {
          selectedUnits.push(unit)
        }
      })
      setUnitList(selectedUnits);
    }
  }, [units])

  return (
    <div>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={0}>
        <Grid item xs={12} style={{ marginBottom: 10 }}>
          <label className={`menuItem`} onClick={(e) => handleSection()} >
            {messages[`maputilities_back`]}
          </label>
        </Grid>
        <div style={{ width: '100%' }}>
          <div>
            <TableView
              data={unitList || []}
              loading={false}
              page={0}
              rowsPerPage={25}
              name="unit-selection-list"
              topSpaceSize={200}
              infiniteScroll={true}
              viewType="list"
              messages={messages}
              itemSelector={false}
              serverSide
              total={units?.length || 0}
              noDataMessage={units?.length === 0 && !show ? messages["realtimemaps_unit_selection_messageTable"] : null}
              onChangePage={() => { }}
              renderRow={(row, index) => {
                let date = getDate(row.unitTime)
                let { duration, formatDate } = getLocalizedDateParts(date, locale, messages['at']);
                return (
                  <div style={{ padding: '10px 15px 0px 2px' }}>
                    <div className='unit-selection-card' style={{ backgroundColor: index % 2 == 0 ? "#f5f5f5" : "rgb(253 253 253)" }}>
                      <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <Grid item>
                          <IconMarker color={getEventColor(row?.eventName)} name={row?.eventName} />
                        </Grid>
                        <Grid item xs zeroMinWidth style={{ marginTop: -20, marginLeft: -10 }}>
                          <Typography
                            noWrap
                            variant="subtitle1"
                            className="unit-selection-title"
                            onClick={() => {
                              mapRef?.current?.showInfoWindow(row.id);
                            }}
                          >
                            {row?.label}
                          </Typography >
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        wrap="nowrap"
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <Grid item xs={12}>
                          {row?.driverName ? <span>{`${messages['realtimemaps_unit_selection_driver']}: ${row?.driverName}`}</span> : <></>}
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        wrap="nowrap"
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <Grid item xs zeroMinWidth>
                          <span>
                            {`${translateEvent(row?.eventName, messages) || ''}  ${duration ? `(${duration})` : ""}`}
                          </span>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        wrap="nowrap"
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <Grid item xs zeroMinWidth>
                          <div style={{
                            minWidth: 150,
                            maxWidth: 300,
                            whiteSpace: 'nowrap',
                            textOverflow: "ellipsis",
                            overflow: 'hidden'
                          }}>
                            <Tooltip title={row?.address || ''} placement="top">
                              <span>{row?.address}</span>
                            </Tooltip>
                          </div>

                        </Grid>
                      </Grid>

                      {row?.landmarkname &&
                        <Grid
                          container
                          wrap="nowrap"
                          direction="row"
                          justifyContent="flex-start"
                          alignItems="center"
                        >
                          <Grid item>
                            <FlagIcon style={{ color: getEventColor(row?.eventName), fontSize: "24px" }} />
                          </Grid>
                          <Grid item xs zeroMinWidth style={{ marginTop: -5, marginLeft: 7 }}>
                            <span>{`${row?.landmarkname}`}</span>
                          </Grid>
                        </Grid>
                      }
                      <Grid
                        container
                        wrap="nowrap"
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <Grid item xs zeroMinWidth style={{ marginTop: row?.landmarkname ? -5 : 0 }}>
                          <span>{formatDate}</span>
                        </Grid>
                      </Grid>
                    </div>
                  </div>
                )
              }}
            />
          </div>
        </div>
      </Grid>
      <SidePanelFooter>
        <Button
          onClick={(e) => handleSection()}
          className="btn-cancel"
        >
          {messages['cancel']}
        </Button>
        <AreaMenu id="areaNotifications" clickClose>
          <Menu className="menu-options-export">
            <div style={{ width: 100, height: 70, borderRadius: 5 }}>
              {exportOptions.map((option, index) => <MenuItem key={index} onClick={() => onExport(option)}>{option}</MenuItem>)}
            </div>
          </Menu>
          <Area>
            <Button
              className="btn-blue"
            >
              {messages['export']}
            </Button>
          </Area>
        </AreaMenu>
      </SidePanelFooter>
    </div>
  )
}

export default UnitSelection;
